import React, { useContext } from 'react';
import { arc as d3Arc } from 'd3-shape';
import { useMemo } from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import { createElement } from 'react';
import { MouseEvent } from 'react';
import { Arc } from 'd3-shape';
import { positionFromAngle } from '@nivo/core';
import { usePropertyAccessor } from '@nivo/core';
import { useTheme } from '@nivo/core';
import { useValueFormatter } from '@nivo/core';
import { useTooltip } from '@nivo/tooltip';
import { RadarColorMapping } from '@nivo/radar';
import { RadarCommonProps } from '@nivo/radar';
import { RadarCustomLayerProps } from '@nivo/radar';
import { RadarDataProps } from '@nivo/radar';
import { RadarSliceTooltipDatum } from '@nivo/radar';
import { RadarSliceTooltipProps } from '@nivo/radar';
import { DefaultPolarChartContext } from 'apps/sigma2-ui/src/components/PolarChart';
import { DefaultPolarDataPoint } from 'apps/sigma2-ui/src/components/PolarChart';
import { getCalibrationLevel } from 'libs/end-to-end/src/calibration-level';
import { CalibrationLevelPill } from 'apps/sigma2-ui/src/components/utils/CalibrationLevelPill';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';

// Based on https://github.com/plouc/nivo/blob/master/packages/radar/src/RadarSlice.tsx
interface DimensionRadarSliceProps<
  DefaultPolarDataPoint extends Record<string, unknown>,
> {
  datum: DefaultPolarDataPoint;
  keys: RadarDataProps<DefaultPolarDataPoint>['keys'];
  index: string | number;
  formatValue: (value: number, context: string) => string;
  colorByKey: Record<string, string>;
  startAngle: number;
  endAngle: number;
  radius: number;
  arcGenerator: Arc<void, { startAngle: number; endAngle: number }>;
  tooltip: RadarCommonProps<DefaultPolarDataPoint>['sliceTooltip'];
  onClick?: () => void;
}

const DimensionRadarSlice = <
  DefaultPolarDataPoint extends Record<string, unknown>,
>({
  datum,
  keys,
  index,
  formatValue,
  colorByKey,
  radius,
  startAngle,
  endAngle,
  arcGenerator,
  tooltip,
  onClick,
}: DimensionRadarSliceProps<DefaultPolarDataPoint>) => {
  const [isHover, setIsHover] = useState(false);
  const theme = useTheme();
  const { showTooltipFromEvent, hideTooltip } = useTooltip();

  const tooltipData = useMemo(() => {
    const data: RadarSliceTooltipDatum[] = keys.map((key) => ({
      color: colorByKey[key],
      id: key,
      value: datum[key] as number,
      formattedValue: formatValue(datum[key] as number, key),
    }));
    data.sort((a, b) => a.value - b.value);
    data.reverse();

    return data;
  }, [datum, keys, formatValue, colorByKey]);

  const showItemTooltip = useCallback(
    (event: MouseEvent<SVGPathElement>) => {
      setIsHover(true);
      showTooltipFromEvent(
        createElement(tooltip, {
          index,
          data: tooltipData,
        }),
        event,
      );
    },
    [showTooltipFromEvent, tooltip, index, tooltipData],
  );

  const hideItemTooltip = useCallback(() => {
    setIsHover(false);
    hideTooltip();
  }, [hideTooltip, setIsHover]);

  const { path, tipX, tipY } = useMemo(() => {
    const position = positionFromAngle(
      startAngle + (endAngle - startAngle) * 0.5 - Math.PI / 2,
      radius,
    );

    return {
      path: arcGenerator({ startAngle, endAngle }) as string,
      tipX: position.x,
      tipY: position.y,
    };
  }, [startAngle, endAngle, radius, arcGenerator]);

  const navigate = useNavigateSafe();

  return (
    <>
      {isHover && (
        <line x1={0} y1={0} x2={tipX} y2={tipY} style={theme.crosshair.line} />
      )}
      <path
        d={path}
        fill="#F00"
        fillOpacity={0}
        onMouseEnter={showItemTooltip}
        onMouseMove={showItemTooltip}
        onMouseLeave={hideItemTooltip}
        onClick={() => {
          if (onClick) onClick();
          return navigate(`${datum['sliceLink']}`);
        }}
      />
    </>
  );
};

// Based on https://github.com/plouc/nivo/blob/master/packages/radar/src/RadarSlices.tsx
interface DimensionRadarSlicesProps<
  DefaultPolarDataPoint extends Record<string, unknown>,
> {
  data: RadarDataProps<DefaultPolarDataPoint>['data'];
  keys: RadarDataProps<DefaultPolarDataPoint>['keys'];
  getIndex: (d: DefaultPolarDataPoint) => string | number;
  formatValue: (value: number, context: string) => string;
  colorByKey: RadarColorMapping;
  radius: number;
  rotation: number;
  angleStep: number;
  tooltip: RadarCommonProps<DefaultPolarDataPoint>['sliceTooltip'];
  onClick?: () => void;
}

const DimensionRadarSlices = <
  DefaultPolarDataPoint extends Record<string, unknown>,
>({
  data,
  keys,
  getIndex,
  formatValue,
  colorByKey,
  radius,
  rotation,
  angleStep,
  tooltip,
  onClick,
}: DimensionRadarSlicesProps<DefaultPolarDataPoint>) => {
  const arc = d3Arc<{ startAngle: number; endAngle: number }>()
    .outerRadius(radius)
    .innerRadius(0);

  const halfAngleStep = angleStep * 0.5;
  let rootStartAngle = rotation - halfAngleStep;

  return (
    <>
      {data.map((datum) => {
        const index = getIndex(datum);
        const startAngle = rootStartAngle;
        const endAngle = startAngle + angleStep;

        rootStartAngle += angleStep;

        return (
          <DimensionRadarSlice
            key={index}
            datum={datum}
            keys={keys}
            index={index}
            formatValue={formatValue}
            colorByKey={colorByKey}
            startAngle={startAngle}
            endAngle={endAngle}
            radius={radius}
            arcGenerator={arc}
            tooltip={tooltip}
            onClick={onClick}
          />
        );
      })}
    </>
  );
};

// This is the custom layer for the radar chart that renders the slices to allow
// for the user to hover AND click on them to navigate to the corresponding slice.
// Ideally, this would be a part of the @nivo/radar library, but it is not.
// Based on https://github.com/plouc/nivo/blob/master/packages/radar/src/Radar.tsx#L146
export const DimensionRadarSliceLayer = (
  props: RadarCustomLayerProps<DefaultPolarDataPoint>,
) => {
  const {
    data,
    keys,
    colorByKey,
    centerX,
    centerY,
    angleStep,
  } = props;
  const formatValue = useValueFormatter<number, string>('>-.2f');
  const rotation = 0;
  const getIndex = usePropertyAccessor<DefaultPolarDataPoint, string>('label');
  const radius = Math.min(centerX, centerY);

  return (
    <g
      key="slices"
      transform={`translate(${centerX}, ${centerY})`}
      className="hover:cursor-pointer"
    >
      <DimensionRadarSlices<DefaultPolarDataPoint>
        data={data}
        keys={keys}
        getIndex={getIndex}
        formatValue={formatValue}
        colorByKey={colorByKey}
        radius={radius}
        rotation={rotation}
        angleStep={angleStep}
        tooltip={DimensionTooltip}
      />
    </g>
  );
};

export const DimensionTooltip = ({ index }: RadarSliceTooltipProps) => {
  const dimension = index;
  const context = useContext(DefaultPolarChartContext);
  const language = useLanguage();

  // Pulls in PolarDataPoint[] from context
  const dimensionData = context.dataPoints.find((x) => x.label === dimension);

  const calibrationScore = isNaN(dimensionData?.calibrationScore ?? 0)
    ? 0
    : dimensionData?.calibrationScore ?? 0;
  const passingTests = dimensionData?.passingTests ?? 0;
  const totalTests = dimensionData?.totalTests ?? 0;

  const calibrationLevel = getCalibrationLevel(calibrationScore);

  return (
    <div className="filter shadow-8px bg-white text-left p-4 font-sans bg-opacity-70 backdrop-blur-sm rounded-md">
      <p className="text-xl font-bold font-sans">{dimension}</p>
      <CalibrationLevelPill
        calibrationLevel={calibrationLevel}
        calibrationFraming={context.calibrationFraming}
      />

      <p>
        {language.polarChartTooltipTestStatement({
          passingTests,
          totalTests,
        })}
      </p>
    </div>
  );
};
